import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  Chip,
  Container,
  Grid,
  IconButton,
  TextField,
  Typography,
  Backdrop,
  Box,
  Button,
  FormControlLabel,
  Modal,
  Switch,
} from "@mui/material";

import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import {
  BCPSquarePlus,
  BCPXmark,
} from "../../../components/icons/bcp/BCPIcons";

import {
  getArmies,
  getGameSystems,
  handlePagingAll,
} from "../../../components/api/APIUtilities";
import ListResponsive from "../../../components/other/ListResponsive";
import { AdminTabs } from "../Admin";
import GameSystemPicker from "../../../components/utilities/gamesystempickers/GameSystemPicker";
import GenericMultiSelect from "../../../components/utilities/GenericMultiSelect";
import GenericPicker from "../../../components/utilities/GenericPicker";
import { postData } from "../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../global/environmentVariables";

const armyLabel = (row) => {
  return row.superFactionObjects?.map((army) => army.name).join(", ");
};

export default function AdminArmyUnits(props) {
  const { activeTab, handleTabChange, authContext } = props;

  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subLoading, setSubLoading] = useState(true);

  const [gameSystems, setGameSystems] = useState(null);
  const [gameSystem, setGameSystem] = useState(null);

  const [armyUnits, setArmyUnits] = useState(null);
  const [armyUnit, setArmyUnit] = useState(null);

  const [armies, setArmies] = useState([]);
  const [armyDict, setArmyDict] = useState({});

  useEffect(() => {
    const go = async () => {
      var apiGameSystems = await getGameSystems();
      if (
        !authContext?.authState?.identity?.isAdmin &&
        authContext?.authState?.identity?.adminGameSystems?.length > 0
      ) {
        apiGameSystems = apiGameSystems.filter((el) =>
          authContext.authState.identity.adminGameSystems.includes(el.id)
        );
      }
      setGameSystems(apiGameSystems);
      setLoading(false);
    };
    go(); // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const go = async () => {
      setSubLoading(true);
      setArmyUnits(null);

      setArmies([]);
      setArmyDict({});

      if (gameSystem) {
        var apiArmies = await getArmies({
          gameType: gameSystem.code,
        });
        var newArmyDict = {};
        var newArmies = [];
        var gameSystemChildFactions = false;
        apiArmies.forEach((army) => {
          var item = {
            value: army.id,
            label: army.name,
            children: [{ value: army.id, label: army.name }],
          };
          newArmyDict[army.id] = {
            id: army.id,
            label: army.name,
          };
          if (army.childArmies?.length) {
            gameSystemChildFactions = true;
            item.children = [
              ...item.children,
              ...army.childArmies.map((child) => {
                newArmyDict[child.id] = {
                  id: child.id,
                  label: child.name,
                };

                return {
                  value: child.id,
                  label: child.name,
                };
              }),
            ];
          }
          newArmies.push(item);
        });

        if (!gameSystemChildFactions) {
          newArmies = newArmies.map((item) => {
            delete item.children;
            return item;
          });
        }

        setArmies(newArmies);
        setArmyDict(newArmyDict);

        var apiArmyUnits = await handlePagingAll(`/armyunits`, {
          limit: 100,
          gameType: gameSystem.code,
        });

        apiArmyUnits.forEach((apiArmyUnit) => {
          apiArmyUnit.superFactionObjects = apiArmyUnit.superFactions?.length
            ? apiArmyUnit.superFactions.map((superFaction) => {
                return {
                  id: newArmyDict[superFaction].id,
                  name: newArmyDict[superFaction].label,
                };
              })
            : [];
          apiArmyUnit.superFactionsLabel = armyLabel(apiArmyUnit);
        });

        setArmyUnits(apiArmyUnits);
      }

      setSubLoading(false);
    };
    go();
  }, [gameSystem]);

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 1,
            backgroundColor: "background.secondary",
          }}
        >
          <Typography variant="h3">Admin Settings</Typography>

          <AdminTabs
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </Grid>
        {gameSystems && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary",
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <GameSystemPicker
                  name="gameType"
                  value={gameSystem?.code || ""}
                  handleChange={(e) => {
                    if (e.target.value) {
                      gameSystems.forEach((gs) => {
                        if (gs.code === e.target.value) {
                          setGameSystem(gs);
                          return true;
                        }
                      });
                    }
                  }}
                  gameSystems={gameSystems}
                />
              </Grid>
            </Grid>
          </Grid>
        )}

        {gameSystem && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary",
            }}
          >
            <ListResponsive
              originalList={subLoading ? null : armyUnits}
              doSearch
              doPages
              initialRowsPerPage={24}
              actionButton={[
                {
                  props: {
                    variant: "contained",
                    color: "primary",
                    onClick: () => {
                      setArmyUnit({});
                    },
                    startIcon: <BCPSquarePlus />,
                  },
                  label: "Add Army Unit",
                },
              ]}
              searchProps={{
                label: "Search Army Unit",
                performSearch: (originalList, searchText) => {
                  if (!searchText || searchText === "") {
                    return originalList;
                  } else {
                    var filteredList = originalList.filter(function (el) {
                      if (el?.name?.toLowerCase().includes(searchText))
                        return true;
                      if (
                        el?.superFactionsLabel
                          ?.toLowerCase()
                          .includes(searchText)
                      )
                        return true;

                      return false;
                    });
                    return filteredList;
                  }
                },
              }}
              rowProps={{
                mapFunction: (row, index) => (
                  <Grid
                    key={index}
                    item
                    xs={12}
                    onClick={(e) => {
                      setArmyUnit(row);
                    }}
                    sx={{
                      p: 1,
                      mb: 1,
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      boxShadow: 1,
                      "&:hover": {
                        cursor: "pointer",
                      },
                    }}
                  >
                    <Typography variant="body1">{row.name}</Typography>
                    <Typography variant="caption">
                      {row.superFactionsLabel}
                    </Typography>
                  </Grid>
                ),
              }}
            />
          </Grid>
        )}
      </Grid>

      <ArmyUnitModal
        gameSystem={gameSystem}
        armyUnit={armyUnit}
        setArmyUnit={setArmyUnit}
        armyUnits={armyUnits}
        setArmyUnits={setArmyUnits}
        armies={armies}
        armyDict={armyDict}
        setProcessing={setProcessing}
      />

      {Boolean(processing || loading) && <ModalLoadingFull />}
    </Container>
  );
}

function ArmyUnitModal(props) {
  const {
    gameSystem,
    armyUnit,
    setArmyUnit,
    armyUnits,
    setArmyUnits,

    armies,
    armyDict,
    setProcessing,
  } = props;

  const [chips, setChips] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [affiliateDict, setAffiliateDict] = useState({});

  const convertChips = (searchName) => {
    if (Array.isArray(searchName)) {
      return searchName;
    }
    if (typeof searchName === "string") {
      return searchName.split(",").map((search) => search.trim());
    }
    return [];
  };

  useEffect(() => {
    if (getEnvironmentVariable("APP_NAME") === "BCP") {
      var affiliateList = [
        { id: "GameKastle", name: "Game Kastle" },
        { id: "TCGplayer", name: "TCGplayer" },
      ];
      var newAffiliateDict = {};
      affiliateList.forEach((affiliate) => {
        newAffiliateDict[affiliate.id] = affiliate;
      });
      setAffiliateDict(newAffiliateDict);
      setAffiliates(
        affiliateList.map((affiliate) => {
          return { value: affiliate.id, label: affiliate.name };
        })
      );
    }
  }, []);

  useEffect(() => {
    if (!armyUnit) {
      setChips([]);
      return;
    }

    setChips(convertChips(armyUnit.searchName));
  }, [armyUnit]);

  return (
    <Modal
      disableAutoFocus
      open={Boolean(armyUnit)}
      onClose={() => {
        setArmyUnit(null);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{ zIndex: 9999 }}
    >
      {armyUnit ? (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setArmyUnit(null);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs>
              <Typography variant="h4" component="h2">
                {armyUnit.id ? "Update" : "Create"} Army Unit
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              id: armyUnit?.id || null,
              name: armyUnit?.name || "",
              searchName: armyUnit?.searchName?.length
                ? armyUnit.searchName.join(",")
                : "",
              active: armyUnit?.active !== undefined ? armyUnit.active : false,
              affiliate: armyUnit?.affiliate || {
                id: "",
              },
              superFactions: armyUnit?.superFactions?.length
                ? armyUnit.superFactions
                : [],
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              name: Yup.string()
                .min(3, "Minimum 3 Characters!")
                .max(255, "Maximum 255 Characters!")
                .required("Required"),
            })}
            onSubmit={async (values) => {
              var apiData;
              setProcessing(true);
              try {
                var sanitizedValues = {
                  name: values.name,
                  active: values.active,
                  superFactions: values.superFactions,
                };
                if (gameSystem) {
                  sanitizedValues.gameSystemId = gameSystem.id;
                }
                if (chips) {
                  sanitizedValues.searchName = chips;
                }
                if (values.affiliate?.id) {
                  sanitizedValues.affiliate = values.affiliate;
                  sanitizedValues.affiliate.name =
                    affiliateDict[values.affiliate?.id]?.name;
                }

                if (values.id) {
                  var body = {
                    set: sanitizedValues,
                  };
                  apiData = await postData(`/armyunits/${values.id}`, {
                    body: body,
                  });
                  postNotification("success", "Army Unit Updated");
                } else {
                  apiData = await postData(`/armyunits`, {
                    body: sanitizedValues,
                  });
                }

                var newArmyUnits;
                if (armyUnit.id) {
                  newArmyUnits = [];
                  armyUnits.forEach((currentArmyUnit) => {
                    if (currentArmyUnit.id !== armyUnit.id) {
                      newArmyUnits.push(currentArmyUnit);
                    } else {
                      newArmyUnits.push(apiData);
                    }
                  });
                } else {
                  newArmyUnits = [apiData, ...armyUnits];
                }
                setArmyUnits(newArmyUnits);
                setArmyUnit(null);
                setProcessing(false);
                return;
              } catch (err) {
                postNotification("error", err);
                setProcessing(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      name="name"
                      label="Name"
                      value={values.name}
                      variant="outlined"
                      fullWidth
                      required
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="searchName"
                      label="Search Options"
                      value={values.searchName}
                      variant="outlined"
                      fullWidth
                      required
                      margin="normal"
                      inputProps={{ style: { textTransform: "lowercase" } }}
                      onBlur={(e) => {
                        handleBlur(e);
                        setChips(convertChips(e?.target?.value.toLowerCase()));
                      }}
                      onChange={(e) => {
                        handleChange(e);
                        setChips(convertChips(e?.target?.value.toLowerCase()));
                      }}
                      error={Boolean(touched.searchName && errors.searchName)}
                      helperText={touched.searchName && errors.searchName}
                    />
                    {chips?.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {chips.map((chip, index) => (
                          <Chip key={index} label={chip} />
                        ))}
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <GenericMultiSelect
                      name="superFactions"
                      label="Factions"
                      value={values.superFactions}
                      setFieldValue={setFieldValue}
                      listDict={armyDict}
                      list={armies}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <GenericPicker
                      label="Affiliate"
                      inclAny={false}
                      name={`affiliate.id`}
                      list={affiliates}
                      value={values.affiliate?.id}
                      handleChange={handleChange}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Active"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.active}
                          onChange={() => {
                            setFieldValue("active", !values.active);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setArmyUnit(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      type="submit"
                      disabled={!values.name}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      ) : (
        <></>
      )}
    </Modal>
  );
}
